import { z } from 'zod';

/**
 * Declarative extraction recipes. One format drives both built-in business directories
 * (Yellow Pages sites, regional directories) and user-created "Any Website" extractions,
 * so adding a directory is a configuration change, not an application change.
 */
export const FIELD_TRANSFORMS = ['text', 'trim', 'phone', 'email', 'url', 'number', 'integer', 'rating', 'lines', 'list'] as const;
export type FieldTransform = (typeof FIELD_TRANSFORMS)[number];

export const SelectorStepSchema = z.object({
  /** CSS selector, relative to the card (list fields) or the document (detail fields). */
  selector: z.string().trim().min(1).max(500).optional(),
  /** Attribute to read (href, src, content, data-*). Text content when omitted. */
  attr: z.string().trim().max(64).optional(),
  /** Optional regex; the first capture group (or whole match) is used. */
  regex: z.string().max(500).optional(),
});
export type SelectorStep = z.infer<typeof SelectorStepSchema>;

export const FieldExtractorSchema = SelectorStepSchema.extend({
  /** Attribute key this field feeds ("name", "phone", "custom.cuisine", ...). */
  key: z.string().trim().min(1).max(80),
  label: z.string().trim().min(1).max(80),
  multiple: z.boolean().default(false),
  transform: z.enum(FIELD_TRANSFORMS).default('text'),
  /** Alternative strategies tried in order when the primary selector yields nothing. */
  fallbacks: z.array(SelectorStepSchema).max(5).default([]),
  /** Fields marked required make a card invalid when missing. */
  required: z.boolean().default(false),
});
export type FieldExtractor = z.infer<typeof FieldExtractorSchema>;

export const PaginationSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('none') }),
  z.object({
    type: z.literal('next_link'),
    selector: z.string().trim().min(1).max(500),
    maxPages: z.number().int().min(1).max(500).default(10),
  }),
  z.object({
    type: z.literal('url_param'),
    param: z.string().trim().min(1).max(40),
    start: z.number().int().min(0).default(1),
    step: z.number().int().min(1).default(1),
    maxPages: z.number().int().min(1).max(500).default(10),
  }),
  z.object({
    /** `{page}` placeholder inside the search URL template. */
    type: z.literal('template'),
    start: z.number().int().min(0).default(1),
    step: z.number().int().min(1).default(1),
    maxPages: z.number().int().min(1).max(500).default(10),
  }),
]);
export type PaginationConfig = z.infer<typeof PaginationSchema>;

export const RecipeSchema = z.object({
  version: z.literal(1).default(1),
  id: z.string().trim().min(1).max(80),
  name: z.string().trim().min(1).max(120),
  /** Site the recipe targets; used for robots.txt, rate limiting and link resolution. */
  baseUrl: z.string().url(),
  /** Country the directory covers (phone parsing default). */
  country: z.string().length(2).optional(),
  render: z.enum(['static', 'browser']).default('static'),
  /**
   * Search URL with placeholders {keyword}, {city}, {area}, {country}, {page}.
   * Omit for recipes that only start from explicit URLs (Any Website).
   */
  searchUrlTemplate: z.string().max(1000).optional(),
  /** Explicit start URLs (Any Website mode). */
  startUrls: z.array(z.string().url()).max(50).default([]),
  list: z.object({
    cardSelector: z.string().trim().min(1).max(500),
    fields: z.array(FieldExtractorSchema).min(1).max(40),
    detailLink: SelectorStepSchema.optional(),
  }),
  pagination: PaginationSchema.default({ type: 'none' }),
  detail: z
    .object({
      fields: z.array(FieldExtractorSchema).max(40).default([]),
      /** Read schema.org JSON-LD (LocalBusiness/Organization) on detail pages. */
      useJsonLd: z.boolean().default(true),
    })
    .optional(),
  /** Read JSON-LD ItemList / LocalBusiness blocks on list pages before using selectors. */
  useJsonLd: z.boolean().default(true),
  /** Minimum delay between requests (merged with robots.txt crawl-delay; the larger wins). */
  minIntervalMs: z.number().int().min(0).max(120_000).default(3000),
  /** Extra text markers identifying a block/challenge page for this site. */
  blockMarkers: z.array(z.string().max(200)).max(20).default([]),
  notes: z.string().max(2000).optional(),
});

export type Recipe = z.infer<typeof RecipeSchema>;
export type RecipeInput = z.input<typeof RecipeSchema>;

export function parseRecipe(input: unknown): Recipe {
  return RecipeSchema.parse(input);
}

/** Expand a search URL template for one query. Placeholders are URL-encoded. */
export function expandSearchUrl(
  template: string,
  values: { keyword?: string; city?: string; area?: string; country?: string; page?: number },
): string {
  const encode = (value: string | number | undefined): string =>
    value === undefined ? '' : encodeURIComponent(String(value)).replace(/%20/g, '+');
  return template
    .replace(/\{keyword\}/g, encode(values.keyword))
    .replace(/\{city\}/g, encode(values.city))
    .replace(/\{area\}/g, encode(values.area))
    .replace(/\{country\}/g, encode(values.country))
    .replace(/\{page\}/g, encode(values.page ?? 1))
    .replace(/\{keyword_slug\}/g, (values.keyword ?? '').toLowerCase().trim().replace(/[^\p{L}\p{N}]+/gu, '-'))
    .replace(/\{city_slug\}/g, (values.city ?? '').toLowerCase().trim().replace(/[^\p{L}\p{N}]+/gu, '-'));
}
